/**
 * Money: reading it in from a form, and writing it out for a reader.
 *
 * Split out of `datetime.ts` once finance made three features validate the same
 * amount. `formatMoney` came with it — an amount and a date only ever shared a
 * file because there was nowhere else to put them.
 */

const LOCALE = 'es-EC';

/**
 * A typed amount: digits, optionally two decimals.
 *
 * Accepts the comma this locale types as a decimal separator; `decimalToNumber`
 * normalises it before it ever reaches the API, which only speaks dots.
 */
export const MONEY = /^\d+([.,]\d{1,2})?$/;

/** A whole number: durations, positions, quantities. */
export const WHOLE = /^\d+$/;

/**
 * Turns a validated amount string into the number the API expects.
 *
 * The counterpart of validating money **as text**, which is the rule this file
 * exists to keep: `z.coerce.number()` turns an empty input into `0`, so a
 * required price silently saves as free instead of failing. The text is checked
 * as text and converted once, on the way out.
 */
export const decimalToNumber = (value: string): number => Number(value.replace(',', '.'));

/** The other direction: an amount from the API, ready to sit in an `<input>`. */
export const toAmountInput = (value: number): string => value.toFixed(2);

/**
 * Amount in the business's currency, e.g. `$120,00`, and a loss as `−$236,50`.
 *
 * The sign goes in front of the symbol: this locale's own pattern writes
 * `$-236,50`, where the minus hides between the symbol and the digits.
 */
export const formatMoney = (value: number, currency = 'USD'): string => {
  const amount = new Intl.NumberFormat(LOCALE, { style: 'currency', currency }).format(
    Math.abs(value)
  );
  return value < 0 ? `−${amount}` : amount;
};

/**
 * The sign an amount input is prefixed with — `$` for USD, `€` for EUR.
 *
 * Read from the business's currency rather than typed into each form, so a
 * salon that bills in another currency is not asked for dollars.
 */
export const currencySymbol = (currency = 'USD'): string =>
  // `narrowSymbol`: this locale spells a foreign currency out ("EUR"), which
  // is right in a sentence and too wide for the box in front of an input.
  new Intl.NumberFormat(LOCALE, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' })
    .formatToParts(0)
    .find((part) => part.type === 'currency')?.value ?? currency;
